import React, { FC, useEffect } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { get } from 'lodash'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import BrandConfig from 'BrandConfig'
import {
  ERROR_CODE_QUOTE_CONSTRAINTS,
  ERROR_CODE_QUOTE_GENERAL,
  ERROR_CODE_QUOTE_VALIDATION,
  ERROR_GENERAL,
  ERROR_TYPE as ET,
} from '../../constants/error-constants'
import { TRANSLATIONS as T } from '../../constants/translations-constants'
import {
  FSMBtnNext,
  LDCardTwoCol,
  LMBgHeader,
  Typography,
  useTranslate,
  withHidden,
} from '@dg-shared'
import { Events } from '../../FSM/shared/constants'
import { logoMax, logoMin } from 'BrandedImages'
import { PartnerName } from '../../types/interface/quote.interface'
import { signOutGetter } from '../../graphql/auth/signOut.getter'
import { CommonPageProps } from '../../types/interface/fsm.interface'
import { fullStorageCleanup } from '@dg-util'

const MobileScreen = withHidden(LMBgHeader, true)
const DesktopScreen = withHidden(LDCardTwoCol, false)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    footer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '& > a': {
        textDecoration: 'none',
      },
    },
    buttonNext: {
      color: theme.palette.secondary.contrastText,
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(3),
      },
    },
    ctaText: {
      textAlign: 'center',
      marginBottom: theme.spacing(1),
    },
    link: (externalLink) => ({
      color: theme.brand.colors.secondary,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: externalLink ? 'underline' : 'none',
      },
    }),
    backBtn: {
      alignSelf: 'start',
      marginBottom: theme.spacing(4),
    },
    indentBigger: {
      marginBottom: theme.spacing(2),
    },
    header: {
      width: '250px',
    },
  })
)

interface ErrorPageProps extends CommonPageProps {
  type: string
  returnPath?: string
  isCurrentHistoryReload?: boolean
}

const ErrorPage: FC<ErrorPageProps> = ({ ...props }) => {
  const { t } = useTranslate(T.ERROR)
  const client = useApolloClient()
  const errorType = get(ET, [props.service.state.event.errorType], ET[ERROR_GENERAL])
  const isSignOutRequired = [ERROR_CODE_QUOTE_VALIDATION, ERROR_CODE_QUOTE_CONSTRAINTS].includes(
    errorType.code
  )

  const signOut = async () => {
    await signOutGetter(client)

    await fullStorageCleanup(client)
  }

  useEffect(() => {
    if (isSignOutRequired) {
      ;(async () => {
        await signOut()
      })()
    }
    // TODO: FIx with using correct deps in [] and useCallback. Left here to not break anything
    // eslint-disable-next-line
  }, [isSignOutRequired])

  const classes = useStyles({ externalLink: true })
  const { send } = props.service
  const nextBtn = (
    <FSMBtnNext
      customClassName={classes.buttonNext}
      handleClick={() => {
        if (isSignOutRequired) {
          window.location.reload()
        } else send(Events.BACK)
      }}
      buttonText={t(`backBtn`)}
    />
  )

  const footer = (
    <footer className={classes.footer}>
      {/*FIXME: Currently used only on FNOL. Work on it later*/}
      {/*{errorType.showBtn ? (!!isCurrentHistoryReload ? reloadBtn : nextBtn) : null}*/}
      {errorType.showBtn && nextBtn}

      {(errorType.code === ERROR_CODE_QUOTE_GENERAL || PARTNER_NAME === PartnerName.instabank) && (
        <>
          <Typography color='textMain' className={classes.ctaText}>
            {t(`CTA.question`)}
          </Typography>
          {BrandConfig.SUPPORT_LINK ? (
            <a href={BrandConfig.SUPPORT_LINK} target='_self' className={classes.link}>
              <Typography color='main' modifiers={['bold']}>
                {t(errorType.linkText)}
              </Typography>
            </a>
          ) : (
            <a
              href={`mailto:${BrandConfig.EMAIL}`}
              target='_blank'
              rel={'noreferrer'}
              className={classes.link}
            >
              <Typography color={'textMain'} className={classes.ctaText}>
                {t('CTA.contactEmail', { email: BrandConfig.EMAIL })}
              </Typography>
            </a>
          )}
        </>
      )}
    </footer>
  )

  const content = (
    <section className={classes.content}>
      {t(`${errorType.content}`, { email: BrandConfig.EMAIL }, 'obj')?.map((el, idx) => (
        <Typography
          key={`${errorType.content}-${idx}`}
          className={classes.indentBigger}
          html={true}
        >
          {el}
        </Typography>
      ))}
      {errorType.emailText && (
        <a
          href={`mailto:${BrandConfig.EMAIL}`}
          target='_blank'
          rel={'noreferrer'}
          className={classes.link}
        >
          <Typography color={'textMain'}>
            {t(errorType.emailText, { email: BrandConfig.EMAIL })}
          </Typography>
        </a>
      )}
    </section>
  )

  return (
    <>
      <MobileScreen
        headerTitle={t(errorType.title)}
        textLogoAlt={t(errorType.title)}
        headerClass={classes.header}
        brandPageBackground={errorType.errorCardMobile}
        imageLogo={logoMin}
        imageGradient
      >
        {content}
        {footer}
      </MobileScreen>
      <DesktopScreen
        imageLogo={logoMax}
        brandPageBackground='error'
        brandPageBackgroundCard='errorCardMain'
        textLogoAlt={t('title')}
        isReverse
      >
        <Typography type='h1'>{t(errorType.title)}</Typography>
        {content}
        {footer}
      </DesktopScreen>
    </>
  )
}

export default ErrorPage
